import axios from "axios";
import { toast } from "react-toastify";

// ============================================================================
// Axios Instance Configuration
// ============================================================================
const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}`, // Base URL from environment variable
  withCredentials: true, // Crucial for sending httpOnly cookies
});

// ============================================================================
// Global State for Error Reporting
// ============================================================================
let lastReportTime = 0;
const REPORT_INTERVAL = 5 * 60 * 1000; // 5 minutes

// ============================================================================
// Request Interceptor
// ============================================================================
axiosInstance.interceptors.request.use(
  (config) => {
    // Add static Product ID
    config.headers["X-ProductID"] = process.env.REACT_APP_PRODUCT_ID;

    const jwtToken = sessionStorage.getItem("jwtToken");
    const tenantId = sessionStorage.getItem("tenantId");

    if (jwtToken) {
      config.headers.Authorization = jwtToken;
      config.headers["X-TenantID"] = tenantId || ""; // Add tenant ID if available
      config.headers["X-ProductID"] = process.env.REACT_APP_PRODUCT_ID; // Ensure Product ID is set
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ============================================================================
// Response Interceptor
// ============================================================================
axiosInstance.interceptors.response.use(
  // On a successful response, check for updated JWT token in headers
  (response) => {
    // Check if response contains a new JWT token and update it
    const newJwtToken =
      response.headers["authorization"] || response.headers["Authorization"];
    if (newJwtToken) {
      sessionStorage.setItem("jwtToken", newJwtToken);
    }
    return response;
  },

  // On an error, handle different status codes.
  async (error) => {
    const originalRequest = error.config;

    // --- ROBUST REFRESH LOGIC ---
    // This block handles token refreshing for expired sessions (401 Unauthorized).
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Mark the request to prevent infinite retry loops.
      originalRequest._retry = true;

      // IMPORTANT: Do not attempt to refresh the token if the original failed request
      // was to the session check or refresh token endpoints themselves.
      const isSessionCheck = originalRequest.url.endsWith("/api/session");
      const isRefreshTokenCall = originalRequest.url.endsWith("/refresh-token");
      const isLoginCall = originalRequest.url.endsWith("/employeeLogin");

      if (isSessionCheck || isRefreshTokenCall || isLoginCall) {
        // If the initial session check, refresh attempt, or login fails, don't retry
        return Promise.reject(error);
      }

      try {
        // Attempt to refresh the session. The server should respond by setting a new httpOnly cookie.
        const refreshResponse = await axiosInstance.post("/refresh-token");

        // Check if refresh response contains a new JWT token
        const newJwtToken =
          refreshResponse.headers["authorization"] ||
          refreshResponse.headers["Authorization"];
        if (newJwtToken) {
          sessionStorage.setItem("jwtToken", newJwtToken);
          // Update the failed request with the new token
          const cleanToken = newJwtToken.replace(/^Bearer\s+/i, "");
          originalRequest.headers.Authorization = `Bearer ${cleanToken}`;
        }

        // Retry the original request with the new token
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // If refreshing the token fails, clear stored tokens and redirect
        sessionStorage.removeItem("jwtToken");
        console.error("Session refresh failed. Logging out.", refreshError);

        // // Redirect to the login page, but only if we are not already there.
        if (
          window.location.pathname !== "/" &&
          window.location.pathname !== "/login"
        ) {
          window.location.href = "/";
        }

        return Promise.reject(refreshError);
      }
    }

    // --- CONFLICT ERROR (409) ---
    // --- CONFLICT ERROR (409) → SHOW BACKEND MESSAGE ---
    if (error.response?.status === 409) {
      toast.error(
        error.response.data?.message ||
          "Conflict occurred. Please check your input.",
      );
      return Promise.reject(error);
    }

    // --- GENERIC USER-FACING ERROR (ALL OTHERS) ---
    toast.error("Some error occurred. Please try again later.");

    // --- ERROR REPORTING LOGIC ---
    // Handle 500 Server Errors with throttled reporting.
    if (error.response?.status === 500) {
      const currentTime = Date.now();
      if (currentTime - lastReportTime >= REPORT_INTERVAL) {
        lastReportTime = currentTime;
        const reportDetails = {
          title: "Server Error: 500",
          description: JSON.stringify({
            message: error.response.data?.message || "Unknown server error",
            status: error.response.status,
            url: error.config.url,
            method: error.config.method,
          }),
        };

        try {
          // Note: This reporting call might also fail if the server is down.
          await axiosInstance.post(`/bugs-report`, reportDetails);
          console.log("500 error report sent successfully.");
        } catch (reportError) {
          console.error("Failed to send 500 error report:", reportError);
        }
      }
    }

    // Handle 501 Not Implemented and redirect to a dedicated error page.
    if (
      error.response?.status === 501 &&
      window.location.pathname !== "/error-500"
    ) {
      window.location.href = "/error-500";
    }

    // For all other errors, reject the promise so they can be handled by the calling code.
    return Promise.reject(error);
  },
);

export default axiosInstance;
